import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useActiveCompanyRole } from '@hooks/usePermission';
import { FiUsers, FiGlobe, FiClock } from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Dropdown, borderedSelectStyles } from '@components/Dropdown';
import { MultiSelectDropdown } from '@components/MultiSelectDropdown';
import { Input } from '@components/Input';
import { addNotification, notificationStates } from '@components/Notification';
import { useTimezones } from '@/hooks/useTimezones';
import { useTeamAttendanceSettings } from '@/hooks/useTeamAttendanceSettings';
import { DEFAULT_WORKING_DAYS } from '@/utils/attendanceUtils';
import teamService from '@/services/team';

import { SettingsPanelHeader } from './SettingsPanelHeader.component';
import {
  SettingsFieldCard,
  settingsFieldCardLayouts,
  SETTINGS_FIELD_CONTROL_WIDTH,
} from './SettingsFieldCard.component';

import styles from './TeamSettingsSection.module.scss';

const findTimezoneOption = (options, timeZone) =>
  timeZone
    ? options.find((option) => option.value === timeZone) || {
        value: timeZone,
        label: timeZone,
      }
    : null;

const WEEKDAY_OPTIONS = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
];

// Pre-filled starting point when a team hasn't set a schedule yet — there's
// no company-level fallback for this (unlike timezone), so saving with
// these defaults is what a team needs before it can turn on Late & Early
// Login Alerts in Alerts & Notifications.
const DEFAULT_WORKING_SCHEDULE = {
  startTime: '10:00 AM',
  endTime: '06:00 PM',
  days: DEFAULT_WORKING_DAYS,
};

// <input type="time"> works in 24h "HH:mm"; scheduleTime storage/backend
// parsing (functions/attendance-alert-helpers.js) expects "h:mm A".
const to24Hour = (t12) => (t12 ? moment(t12, 'h:mm A').format('HH:mm') : '');
const to12Hour = (t24) => (t24 ? moment(t24, 'HH:mm').format('h:mm A') : '');

export const TeamSettingsSection = () => {
  const userProfile = useSelector((state) => state.auth?.profile);
  const companyId = userProfile?.activeCompany?.companyId;
  const userId = userProfile?.id;
  const role = useActiveCompanyRole();
  const activeTeamId = useSelector((state) => state.team?.active?.activeTeamid);

  const timezoneOptions = useTimezones();

  const [teamOptions, setTeamOptions] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [savedTimezone, setSavedTimezone] = useState(null);
  const [loadingTimezone, setLoadingTimezone] = useState(false);

  const { workingSchedule: savedWorkingSchedule, loading: loadingSchedule } =
    useTeamAttendanceSettings(selectedTeam?.value);

  const [workingSchedule, setWorkingSchedule] = useState(
    DEFAULT_WORKING_SCHEDULE
  );

  const [saving, setSaving] = useState(false);

  // Sync draft state from the live doc whenever the selected team or its
  // saved values change — mirrors how selectedTimezone/savedTimezone sync.
  useEffect(() => {
    setWorkingSchedule(savedWorkingSchedule || DEFAULT_WORKING_SCHEDULE);
  }, [selectedTeam?.value, savedWorkingSchedule]);

  // Load the teams this user is allowed to configure — every team in the
  // company for Admins, only their own team(s) for Managers (branches inside
  // teamService.getMemberTeamCollection based on `role`).
  useEffect(() => {
    let cancelled = false;

    const fetchTeams = async () => {
      if (!companyId || !userId) return;
      setLoadingTeams(true);

      const res = await teamService.getMemberTeamCollection({
        companyId,
        userId,
        role,
        companyRole: role,
      });

      if (cancelled) return;

      if (res.success) {
        const options = res.data.map((team) => ({
          value: team.docId,
          label: team.Team,
        }));
        setTeamOptions(options);
        setSelectedTeam(
          options.find((option) => option.value === activeTeamId) ||
            options[0] ||
            null
        );
      } else {
        addNotification({
          message: 'Failed to load teams. Please try again.',
          level: notificationStates.error,
        });
      }
      setLoadingTeams(false);
    };

    fetchTeams();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userId, role]);

  // Fetch the selected team's saved timezone straight from teamDatabase —
  // not read off the option object, since the company-wide team list here
  // doesn't carry a timeZone field.
  useEffect(() => {
    let cancelled = false;

    const fetchTimezone = async () => {
      if (!selectedTeam?.value) {
        setSelectedTimezone(null);
        setSavedTimezone(null);
        return;
      }
      setLoadingTimezone(true);

      const res = await teamService.getTeamTimezone(selectedTeam.value);
      if (cancelled) return;

      const timeZone = res.success ? res.timeZone : null;
      setSelectedTimezone(findTimezoneOption(timezoneOptions, timeZone));
      setSavedTimezone(timeZone);
      setLoadingTimezone(false);
    };

    fetchTimezone();
    return () => {
      cancelled = true;
    };
  }, [selectedTeam?.value, timezoneOptions]);

  const hasTimezoneChanges = Boolean(
    selectedTeam && selectedTimezone && selectedTimezone.value !== savedTimezone
  );
  const hasScheduleChanges = Boolean(
    selectedTeam &&
    JSON.stringify(workingSchedule) !==
      JSON.stringify(savedWorkingSchedule || DEFAULT_WORKING_SCHEDULE)
  );
  const hasChanges = hasTimezoneChanges || hasScheduleChanges;

  const handleCancel = () => {
    setSelectedTimezone(findTimezoneOption(timezoneOptions, savedTimezone));
    setWorkingSchedule(savedWorkingSchedule || DEFAULT_WORKING_SCHEDULE);
  };

  const handleSaveChanges = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);

    const results = await Promise.all([
      hasTimezoneChanges
        ? teamService.updateTeamTimezone(
            selectedTeam.value,
            selectedTimezone.value
          )
        : Promise.resolve({ success: true }),
      hasScheduleChanges
        ? teamService.saveTeamAttendanceSettings(selectedTeam.value, {
            workingSchedule,
          })
        : Promise.resolve({ success: true }),
    ]);

    const failed = results.find((res) => !res.success);
    if (!failed) {
      if (hasTimezoneChanges) setSavedTimezone(selectedTimezone.value);
      addNotification({
        message: `Settings saved for "${selectedTeam.label}"`,
        level: notificationStates.success,
      });
    } else {
      addNotification({
        message: failed.error || 'Failed to save settings. Please try again.',
        level: notificationStates.error,
      });
    }
    setSaving(false);
  };

  return (
    <>
      <SettingsPanelHeader
        icon={<FiUsers size={20} color="var(--indigo-600)" />}
        title="Team Settings"
        description="Manage preferences and configurations for your teams. Changes will apply to the selected team."
      />

      <SettingsFieldCard
        layout={settingsFieldCardLayouts.row}
        icon={<FiUsers size={16} color="var(--indigo-600)" />}
        label="Select Team"
        helperText="Choose the team you want to configure."
      >
        <Dropdown
          options={teamOptions}
          value={selectedTeam}
          onChange={setSelectedTeam}
          isLoading={loadingTeams}
          isDisabled={saving}
          width={SETTINGS_FIELD_CONTROL_WIDTH}
          selectStyles={borderedSelectStyles}
          placeholder="Select a team"
        />
      </SettingsFieldCard>

      <div className={styles.configHeading}>
        <Text
          variant={textVariantsEnum.Text_md}
          fontWeight={fontWeightEnum.Semibold}
          color="gray-900"
        >
          Team Configurations
        </Text>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Normal}
          color="gray-600"
        >
          Manage settings and preferences for the selected team.
        </Text>
      </div>

      <div className={styles.configFields}>
        <SettingsFieldCard
          layout={settingsFieldCardLayouts.row}
          icon={<FiGlobe size={16} color="var(--blue-600)" />}
          iconBg="blue-100"
          label="Timezone"
          helperText="Set the timezone for this team. This will be used for time tracking, reports and schedules."
        >
          <Dropdown
            options={timezoneOptions}
            value={selectedTimezone}
            onChange={setSelectedTimezone}
            isLoading={loadingTimezone}
            isDisabled={saving || !selectedTeam}
            width={SETTINGS_FIELD_CONTROL_WIDTH}
            selectStyles={borderedSelectStyles}
            placeholder="Not set — select a timezone"
          />
        </SettingsFieldCard>

        <SettingsFieldCard
          layout={settingsFieldCardLayouts.row}
          icon={<FiClock size={16} color="var(--blue-600)" />}
          iconBg="blue-100"
          label="Scheduled Working Hours"
          helperText="When this team is expected to be online. Used for late/early check-in alerts, configured in Alerts & Notifications."
        >
          <div className={styles.timeRangeRow}>
            <Input
              type="time"
              value={to24Hour(workingSchedule.startTime)}
              onChange={(e) =>
                setWorkingSchedule((prev) => ({
                  ...prev,
                  startTime: to12Hour(e.target.value),
                }))
              }
              disabled={saving || !selectedTeam || loadingSchedule}
            />
            <Text variant={textVariantsEnum.Text_sm} color="gray-500">
              to
            </Text>
            <Input
              type="time"
              value={to24Hour(workingSchedule.endTime)}
              onChange={(e) =>
                setWorkingSchedule((prev) => ({
                  ...prev,
                  endTime: to12Hour(e.target.value),
                }))
              }
              disabled={saving || !selectedTeam || loadingSchedule}
            />
          </div>
        </SettingsFieldCard>

        <SettingsFieldCard
          layout={settingsFieldCardLayouts.row}
          icon={<FiClock size={16} color="var(--blue-600)" />}
          iconBg="blue-100"
          label="Working Days"
          helperText="Days lateness/earliness is evaluated on — check-ins on other days are never flagged."
        >
          <MultiSelectDropdown
            options={WEEKDAY_OPTIONS}
            value={WEEKDAY_OPTIONS.filter((opt) =>
              workingSchedule.days.includes(opt.value)
            )}
            onChange={(selected) =>
              setWorkingSchedule((prev) => ({
                ...prev,
                days: (selected || []).map((opt) => opt.value),
              }))
            }
            placeholder="Select working days"
          />
        </SettingsFieldCard>
      </div>

      <div className={styles.actionsRow}>
        <Button
          text="Cancel"
          variant={buttonVariants.secondary}
          onClick={handleCancel}
          disabled={saving}
        />
        <Button
          text="Save Changes"
          variant={buttonVariants.primary}
          onClick={handleSaveChanges}
          loading={saving}
          disabled={!hasChanges}
        />
      </div>
    </>
  );
};

TeamSettingsSection.displayName = 'TeamSettingsSection';
